// Estado do diálogo de widgets <-> `commercial_widget` da API (contrato da spec 166).

export const MAX_CONDITIONS = 5;
export const TITLE_MAX_LENGTH = 60;

export const VISUALIZATIONS = [
  'number',
  'rate',
  'bars',
  'doughnut',
  'table',
  'line',
];
export const UNITS = ['contact', 'company', 'deal'];
export const GROUPED_VISUALIZATIONS = ['bars', 'doughnut', 'table'];

const VISIBLE_ENTITIES = {
  deal: ['deal', 'contact', 'company'],
  contact: ['contact', 'company', 'deal'],
  company: ['company', 'deal'],
};

const DEFAULT_OPERATOR = {
  label: 'any',
  pipeline: 'in',
  stage: 'in',
  stage_type: 'is',
  responsible: 'in',
};

const GROUP_BY_TYPES = ['list', 'multiple_choice', 'checkbox'];
const VALUELESS_OPERATORS = ['filled', 'empty'];

export const visibleEntities = unit => VISIBLE_ENTITIES[unit];

export const allowedMeasureTypes = visualization =>
  visualization === 'number'
    ? ['count', 'sum', 'avg', 'min', 'max']
    : ['count', 'sum'];

export const fieldRef = field =>
  `${field.custom ? 'custom' : 'standard'}:${field.key}`;

const parseFieldRef = ref => {
  const [kind, ...key] = ref.split(':');
  return { key: key.join(':'), custom: kind === 'custom' };
};

export const entityFields = (catalog, entity) =>
  catalog?.fields?.[entity] || [];

export const findField = (catalog, entity, ref) =>
  entityFields(catalog, entity).find(field => fieldRef(field) === ref);

export const numericFields = (catalog, unit) =>
  entityFields(catalog, unit).filter(field => field.numeric);

export const groupableFields = (catalog, unit) =>
  entityFields(catalog, unit).filter(field =>
    GROUP_BY_TYPES.includes(field.type)
  );

let conditionUid = 0;
const nextUid = () => {
  conditionUid += 1;
  return conditionUid;
};

export const newCondition = (source, entity) => ({
  uid: nextUid(),
  source,
  entity,
  operator: DEFAULT_OPERATOR[source] || '',
  values: [],
  field: '',
});

// Chave da opção de fonte no seletor: etiqueta e campo dependem da entidade.
export const sourceKey = condition =>
  ['label', 'field'].includes(condition.source)
    ? `${condition.source}:${condition.entity}`
    : condition.source;

export const parseSourceKey = key => {
  const [source, entity] = key.split(':');
  return { source, entity: entity || 'deal' };
};

export const isConditionComplete = (condition, catalog) => {
  if (condition.source !== 'field') return condition.values.length > 0;
  if (!findField(catalog, condition.entity, condition.field)) return false;
  if (VALUELESS_OPERATORS.includes(condition.operator)) return true;
  return condition.values.length > 0 && condition.values[0] !== '';
};

const conditionToApi = condition => {
  const payload = {
    source: condition.source,
    entity: condition.entity,
    operator: condition.operator,
    values: condition.values,
  };
  if (condition.source !== 'field') return payload;
  return { ...payload, ...parseFieldRef(condition.field) };
};

const conditionFromApi = condition => ({
  uid: nextUid(),
  source: condition.source,
  entity: condition.entity,
  operator: condition.operator,
  values: [...condition.values],
  field: condition.source === 'field' ? fieldRef(condition) : '',
});

export const defaultForm = () => ({
  title: '',
  visualization: 'number',
  unit: 'deal',
  measureType: 'count',
  measureField: '',
  dateBasis: 'auto',
  conditions: [],
  groupBy: '',
  numeratorId: '',
  denominatorId: '',
});

const buildMeasure = form =>
  form.measureType === 'count'
    ? { type: 'count' }
    : {
        type: form.measureType,
        field: { entity: form.unit, ...parseFieldRef(form.measureField) },
      };

const buildGroupBy = form => {
  if (!form.groupBy.startsWith('field:')) {
    return { source: form.groupBy, entity: form.unit };
  }
  return {
    source: 'field',
    entity: form.unit,
    ...parseFieldRef(form.groupBy.slice('field:'.length)),
  };
};

const buildConfig = form => {
  if (form.visualization === 'rate') {
    return {
      numerator_id: form.numeratorId,
      denominator_id: form.denominatorId,
    };
  }
  const config = {
    unit: form.unit,
    measure: buildMeasure(form),
    date_basis: form.dateBasis,
    conditions: form.conditions.map(conditionToApi),
  };
  if (GROUPED_VISUALIZATIONS.includes(form.visualization)) {
    config.group_by = buildGroupBy(form);
  }
  return config;
};

// O client (`commercialWidgets.js`) embrulha em `commercial_widget`.
export const buildPayload = form => ({
  title: form.title.trim(),
  visualization: form.visualization,
  config: buildConfig(form),
});

const parseGroupBy = groupBy => {
  if (!groupBy) return '';
  return groupBy.source === 'field'
    ? `field:${fieldRef(groupBy)}`
    : groupBy.source;
};

// Widget salvo -> estado do formulário (pré-preenchimento da edição).
export const formFromWidget = widget => {
  const { config } = widget;
  const form = {
    ...defaultForm(),
    title: widget.title,
    visualization: widget.visualization,
  };
  if (widget.visualization === 'rate') {
    return {
      ...form,
      numeratorId: config.numerator_id,
      denominatorId: config.denominator_id,
    };
  }
  return {
    ...form,
    unit: config.unit,
    measureType: config.measure.type,
    measureField: config.measure.field ? fieldRef(config.measure.field) : '',
    dateBasis: config.date_basis || 'auto',
    conditions: (config.conditions || []).map(conditionFromApi),
    groupBy: parseGroupBy(config.group_by),
  };
};

export const isFormComplete = (form, catalog) => {
  if (!form.title.trim()) return false;
  if (form.visualization === 'rate') {
    return (
      !!form.numeratorId &&
      !!form.denominatorId &&
      form.numeratorId !== form.denominatorId
    );
  }
  if (form.measureType !== 'count' && !form.measureField) return false;
  if (GROUPED_VISUALIZATIONS.includes(form.visualization) && !form.groupBy) {
    return false;
  }
  return form.conditions.every(condition =>
    isConditionComplete(condition, catalog)
  );
};
